/**
 * Presence line formatter for the waiting room's "who's already here" line.
 * Pure module (no DOM, no network) so it stays unit-testable, like
 * worldSession.js.
 *
 * SECURITY: display names are self-chosen (hostile) input and this function
 * deliberately does NOT escape them — callers must render the returned string
 * via `textContent`, never `innerHTML`.
 */

/**
 * Format a Meet-style presence line from a live count and up to 3 display
 * names (the shape of GET /api/video-sessions/presence).
 *
 * - 0 live: "No one else is here yet. You'll be the first."
 * - All live participants named: "Alice is here" / "Alice and Bob are here" /
 *   "Alice, Bob and Carol are here"
 * - More live than named: "Alice, Bob and 2 others are here" ("1 other")
 * - No usable names: "3 people are already here" ("1 person")
 *
 * @param {number} live - live participant count
 * @param {string[]} [names] - display names (blank/non-string entries ignored)
 * @returns {string}
 */
export function formatPresenceLine(live, names = []) {
  if (!(live > 0)) return "No one else is here yet. You'll be the first.";

  const usable = (Array.isArray(names) ? names : [])
    .filter((n) => typeof n === 'string' && n.trim() !== '')
    .slice(0, Math.min(3, live));

  if (usable.length === 0) {
    return live === 1 ? '1 person is already here' : `${live} people are already here`;
  }

  if (live > usable.length) {
    // Overflow: show at most 2 names, fold the rest into "N others".
    const shown = usable.slice(0, 2);
    const others = live - shown.length;
    const othersLabel = others === 1 ? '1 other' : `${others} others`;
    return `${shown.join(', ')} and ${othersLabel} are here`;
  }

  if (usable.length === 1) return `${usable[0]} is here`;
  if (usable.length === 2) return `${usable[0]} and ${usable[1]} are here`;
  return `${usable[0]}, ${usable[1]} and ${usable[2]} are here`;
}
